import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Play, X, Loader2, CheckCircle2,
  AlertTriangle, Trash2, RotateCcw, Database, Zap, TrendingUp,
} from "lucide-react";

const STATUS = {
  create:          { label: "Create",   cls: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/30" },
  skip_dup:        { label: "Exists",   cls: "bg-secondary text-muted-foreground border-border" },
  low_conf_review: { label: "Low conf", cls: "bg-[#F59E0B]/10 text-[#F59E0B] border-[#F59E0B]/30" },
  dropped:         { label: "Dropped",  cls: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
};

function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.skip_dup;
  return (
    <span className={`font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-lg border ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function AdminSeedCatalog() {
  const [targetCount, setTargetCount] = useState(200);
  const [sources, setSources] = useState({ signal: true, onet: true });
  const [building, setBuilding] = useState(false);
  const [plan, setPlan] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [run, setRun] = useState(null);
  const [launching, setLaunching] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const runActive = run && ["queued", "running"].includes(run.status);

  const buildPlan = async () => {
    const srcs = Object.entries(sources).filter(([, v]) => v).map(([k]) => k);
    if (srcs.length === 0) { toast.error("Pick at least one source"); return; }
    setBuilding(true); setPlan(null); setRows([]); setRun(null);
    try {
      const { data } = await api.post(
        "/admin/roles/seed/plan",
        { target_count: Number(targetCount) || 200, sources: srcs },
        { timeout: 300000 },
      );
      setPlan(data);
      setRows(data.rows || []);
      toast.success(`Plan built — ${data.summary?.create || 0} roles to create`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not build plan");
    } finally { setBuilding(false); }
  };

  const updateRow = (soc, patch) =>
    setRows((rs) => rs.map((r) => (r.soc_code === soc ? { ...r, ...patch } : r)));

  const toggleDrop = (r) => {
    if (!r.soc_code) return;
    if (r.status === "dropped") updateRow(r.soc_code, { status: r._prev || "create" });
    else updateRow(r.soc_code, { status: "dropped", _prev: r.status });
  };

  const saveEdits = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const payload = {
        rows: rows.filter((r) => r.soc_code).map((r) => ({
          soc_code: r.soc_code,
          industry: r.industry,
          family: r.family,
          drop: r.status === "dropped",
        })),
      };
      const { data } = await api.patch(`/admin/roles/seed/plan/${plan.plan_id}`, payload);
      setPlan((p) => ({ ...p, summary: data.summary }));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save edits");
      return false;
    } finally { setSaving(false); }
  };

  const pollRun = (runId) => {
    clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/roles/seed/run/${runId}`);
        setRun(data);
        if (["done", "failed", "cancelled"].includes(data.status)) {
          clearInterval(pollRef.current);
        }
      } catch { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  };

  const startRun = async () => {
    const ok = await saveEdits();
    if (!ok) return;
    setLaunching(true);
    try {
      const { data } = await api.post("/admin/roles/seed/run", { plan_id: plan.plan_id });
      toast.success(`Run started — generating ${data.queued} roles`);
      pollRun(data.run_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start run");
    } finally { setLaunching(false); }
  };

  const cancelRun = async () => {
    if (!run) return;
    try {
      await api.post(`/admin/roles/seed/run/${run.run_id}/cancel`);
      toast.success("Cancelling after the current roles finish…");
    } catch { toast.error("Could not cancel"); }
  };

  const createCount = rows.filter((r) => r.status === "create").length;
  const lowConfCount = rows.filter((r) => r.status === "low_conf_review").length;
  const skipCount = rows.filter((r) => r.status === "skip_dup").length;

  const done = run?.done_socs?.length || 0;
  const total = run?.total || createCount || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <main className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin" className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-[#1D4ED8] inline-flex items-center gap-1.5 transition-colors group">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-0.5 transition-transform" /> Admin
        </Link>
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-[#1D4ED8]" /> Bulk Seed Catalog
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Auto-create O*NET-grounded roles from candidate demand + common occupations. Build a plan,
          review it, then generate full question banks in the background.
        </p>
      </div>

      {/* Step 1 — Build plan */}
      <div className="rounded-xl border border-border bg-white p-6 mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8] mb-4">Step 1 · Build a plan</div>
        <div className="flex flex-wrap items-end gap-6">
          <div>
            <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Target roles</label>
            <Input type="number" min={1} max={500} value={targetCount}
              onChange={(e) => setTargetCount(e.target.value)}
              className="rounded-xl w-28" disabled={building || runActive} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Sources</label>
            <div className="flex gap-2">
              {[["signal", "Candidate demand", Zap], ["onet", "O*NET common", Database]].map(([k, label, Icon]) => (
                <button key={k} type="button" disabled={building || runActive}
                  onClick={() => setSources((s) => ({ ...s, [k]: !s[k] }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-semibold transition-all disabled:opacity-50 ${
                    sources[k] ? "bg-[#1D4ED8]/10 border-[#1D4ED8]/40 text-[#1D4ED8]" : "border-border text-muted-foreground hover:border-[#1D4ED8]/30"
                  }`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                  {sources[k] && <CheckCircle2 className="w-3 h-3" />}
                </button>
              ))}
            </div>
          </div>
          <Button onClick={buildPlan} disabled={building || runActive}
            className="rounded-xl bg-[#1D4ED8] hover:bg-[#1E40AF] h-11 px-5">
            {building
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building plan…</>
              : <><Sparkles className="w-4 h-4 mr-2" /> Build plan</>}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3 font-mono">
          Dry run — resolves &amp; dedupes titles and proposes industry/family. No roles created yet.
        </p>
      </div>

      {/* Step 2 — Review */}
      {plan && (
        <div className="rounded-xl border border-border bg-white mb-6 overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8]">Step 2 · Review</div>
              <div className="flex items-center gap-2">
                <StatChip label="Create" value={createCount} cls="text-[#10B981]" />
                <StatChip label="Low conf" value={lowConfCount} cls="text-[#F59E0B]" />
                <StatChip label="Exists" value={skipCount} cls="text-muted-foreground" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={saveEdits} disabled={saving || runActive}
                className="rounded-xl">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                Save edits
              </Button>
              <Button size="sm" onClick={startRun} disabled={launching || runActive || createCount === 0}
                className="rounded-xl bg-[#10B981] hover:bg-[#059669]">
                {launching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                Generate {createCount} roles
              </Button>
            </div>
          </div>

          {/* rows */}
          <div className="max-h-[26rem] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-secondary/60 backdrop-blur font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Role</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium w-44">Industry</th>
                  <th className="text-left px-3 py-2 font-medium w-44">Family</th>
                  <th className="text-center px-3 py-2 font-medium">Demand</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const editable = r.soc_code && ["create", "low_conf_review", "dropped"].includes(r.status);
                  return (
                    <tr key={r.soc_code || `row-${i}`}
                      className={`border-b border-border/60 ${r.status === "dropped" ? "opacity-50" : ""} ${r.status === "skip_dup" ? "bg-secondary/20" : ""}`}>
                      <td className="px-4 py-2.5 min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className={`font-semibold truncate ${r.status === "dropped" ? "line-through" : ""}`}>{r.canonical_name}</span>
                          {r.bright_outlook && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#B45309] font-mono text-[8px] uppercase tracking-wider whitespace-nowrap flex-shrink-0">
                              <TrendingUp className="w-2.5 h-2.5" /> High Demand
                            </span>
                          )}
                          {r.status === "skip_dup" && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border border-border bg-secondary text-muted-foreground font-mono text-[8px] uppercase tracking-wider whitespace-nowrap flex-shrink-0">
                              Added
                            </span>
                          )}
                        </div>
                        <div className="font-mono text-[10px] text-muted-foreground">
                          {r.soc_code || "no SOC"} · {r.source}
                        </div>
                      </td>
                      <td className="px-3 py-2.5"><StatusBadge status={r.status} /></td>
                      <td className="px-3 py-2.5">
                        {editable ? (
                          <Input value={r.industry || ""} onChange={(e) => updateRow(r.soc_code, { industry: e.target.value })}
                            disabled={runActive} className="rounded-lg h-8 text-xs" placeholder="—" />
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2.5">
                        {editable ? (
                          <Input value={r.family || ""} onChange={(e) => updateRow(r.soc_code, { family: e.target.value })}
                            disabled={runActive} className="rounded-lg h-8 text-xs" placeholder="—" />
                        ) : <span className="text-muted-foreground text-xs">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-center font-mono text-xs text-muted-foreground">
                        {r.demand_count > 0 ? `×${r.demand_count}` : "—"}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {r.soc_code && r.status !== "skip_dup" && (
                          <button onClick={() => toggleDrop(r)} disabled={runActive}
                            title={r.status === "dropped" ? "Restore" : "Drop"}
                            className="text-muted-foreground hover:text-[#E11D48] disabled:opacity-40 transition-colors">
                            {r.status === "dropped" ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Step 3 — Run progress */}
      {run && (
        <div className="rounded-xl border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1D4ED8]">Step 3 · Generation</div>
            <div className="flex items-center gap-3">
              <RunStatus status={run.status} />
              {runActive && (
                <Button variant="outline" size="sm" onClick={cancelRun} className="rounded-xl">
                  <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                </Button>
              )}
            </div>
          </div>

          <div className="w-full h-2.5 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-[#10B981] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex items-center justify-between mt-2 font-mono text-[11px] text-muted-foreground">
            <span>{done} / {total} processed</span>
            <span>{pct}%</span>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-5">
            <ResultTile label="Created" value={run.results?.created || 0} cls="text-[#10B981]" />
            <ResultTile label="Skipped (dup)" value={run.results?.skipped_dup || 0} cls="text-muted-foreground" />
            <ResultTile label="Failed" value={run.results?.failed || 0} cls="text-[#E11D48]" />
          </div>

          {run.errors?.length > 0 && (
            <div className="mt-4 rounded-xl border border-[#E11D48]/30 bg-[#E11D48]/5 p-3 max-h-32 overflow-y-auto">
              <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-[#E11D48] mb-1.5">
                <AlertTriangle className="w-3 h-3" /> {run.errors.length} error{run.errors.length !== 1 ? "s" : ""}
              </div>
              {run.errors.slice(0, 20).map((e, i) => (
                <div key={i} className="font-mono text-[10px] text-[#E11D48]/80 truncate">
                  {e.soc_code ? `${e.soc_code}: ` : ""}{e.error}
                </div>
              ))}
            </div>
          )}

          {run.status === "done" && (
            <p className="mt-4 text-sm text-[#10B981] font-medium flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4" /> Done — new roles are live.
              <Link to="/admin/roles" className="text-[#1D4ED8] hover:underline ml-1">View roles →</Link>
            </p>
          )}
        </div>
      )}
    </main>
  );
}

function StatChip({ label, value, cls }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[11px]">
      <span className={`font-bold ${cls}`}>{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </span>
  );
}

function ResultTile({ label, value, cls }) {
  return (
    <div className="rounded-xl border border-border p-3 text-center">
      <div className={`font-display font-black text-2xl ${cls}`}>{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function RunStatus({ status }) {
  const map = {
    queued:    { label: "Queued",    cls: "text-muted-foreground", spin: true },
    running:   { label: "Running",   cls: "text-[#1D4ED8]", spin: true },
    done:      { label: "Done",      cls: "text-[#10B981]", spin: false },
    failed:    { label: "Failed",    cls: "text-[#E11D48]", spin: false },
    cancelled: { label: "Cancelled", cls: "text-[#F59E0B]", spin: false },
  };
  const s = map[status] || map.queued;
  return (
    <span className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider ${s.cls}`}>
      {s.spin && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {s.label}
    </span>
  );
}
